// Workbook identity (sculpt-api PLAN-workbook-identity.md).
//
// `pcp_agent_v2_workbook_id` is a UUID stored as a custom document property, created when the user starts the first
// index or sends the first message (never on open). Custom properties are COPIED on Save As, so two copies of a file
// carry the same id: the server tells them apart by the file's path and asks the user which workbook this is (the
// identity question, `WorkbookIndexer`). Office.js access goes through the executor (the single seam).
import { excelToolExecutor } from './ExcelToolExecutor';
import { isCellEditModeError } from './officeErrors';

export const WORKBOOK_ID_PROPERTY = 'pcp_agent_v2_workbook_id';

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  // Fallback for older WebViews.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Read the id without creating it (null when the workbook was never used with the agent). */
export async function getWorkbookId(): Promise<string | null> {
  return excelToolExecutor.getCustomProperty(WORKBOOK_ID_PROPERTY);
}

/** Read-or-create the id. Creation is best-effort: on a read-only file we still return a
 *  session-scoped id so the chat works (history just won't be re-attached next time).
 *  A read that fails (the user typing in a cell) throws: it is not a file without an id, and minting one there
 *  moved the pane to a workbook of its own, every open conversation turning read-only under the user's feet. */
export async function ensureWorkbookId(): Promise<string> {
  const existing = await getWorkbookId();
  if (existing) return existing;
  const id = uuid();
  console.warn('[agent-v2] the file carries no workbook id: a new one is written', id);
  await writeWorkbookId(id);
  return id;
}

/** Write `id` into the file: the id the server answered with (a fork, another workbook, a file recognised by its
 *  path), or the same id again. False when the write failed — the file is read-only, and the server is told so
 *  rather than asked a question whose answer could not be written into it (D16). */
export async function writeWorkbookId(id: string): Promise<boolean> {
  try {
    await excelToolExecutor.setCustomProperty(WORKBOOK_ID_PROPERTY, id);
    return true;
  } catch (err) {
    // A cell edit refuses the write for now: the file is not read-only, and the caller waits the edit out.
    if (isCellEditModeError(err)) throw err;
    console.warn('[agent-v2] could not write the workbook id (read-only file?)', err);
    return false;
  }
}
